import React, { useMemo } from 'react';

import { IconButton, Portal, Typography, makeStyles } from '@material-ui/core';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import clsx from 'clsx';

import { formatRemainingTime, isSheetsTimerOverLimit } from 'constants/dictionaries/sheetsTimer';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import LAYOUT_SIZES from 'constants/styles/LAYOUT_SIZES';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';
import useFeatureChecker from 'services/common/useFeatureChecker/useFeatureChecker';
import useSheetsStore from 'storages/sheets/sheets';
import useSheetsTimerStore from 'storages/sheetsTimer/sheetsTimer';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: 'none',
    minWidth: '200px',
    marginRight: theme.spacing(4),
    padding: theme.spacing(1, 2),
    transition: 'height .3s ease, padding .3s ease',
    height: ({ isStuck }: { isStuck: boolean }) => (isStuck ? '50px' : '95px'),
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
      marginRight: 0,
      height: 'auto',
      padding: theme.spacing(0.5, 1),
    },
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  label: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  time: {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
  },
  overLimit: {
    color: theme.palette.warning.dark,
  },
  controlButton: {
    padding: theme.spacing(0.5),
  },
  pomoceWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.primary.light}`,
    borderRadius: theme.shape.borderRadius,
  },
  mobileFixedWrapper: {
    position: 'fixed',
    bottom: LAYOUT_SIZES.FOOTER.sm,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[2],
    zIndex: 101,
  },
}));

export const SHEETS_TIMER_MOBILE_BAR_HEIGHT = 36;

export const useIsSheetsTimerActive = (): boolean => {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.SHEETS_TIMER);
  const { isSheet } = useFeatureChecker();
  const { fetched } = useSheetsStore();
  const { durationMinutes, activeSheetKey } = useSheetsTimerStore();

  return isEnabled && isSheet && fetched && durationMinutes != null && activeSheetKey != null;
};

type Props = {
  isStuck?: boolean;
  variant?: 'desktop' | 'pomoce' | 'mobile-fixed';
};

type TimerContentProps = {
  formattedTime: string;
  isOverLimit: boolean;
  isRunning: boolean;
  onPause: () => void;
  onResume: () => void;
  classes: ReturnType<typeof useStyles>;
  labelLayout?: 'inline' | 'stacked';
};

const TimerContent: React.FC<TimerContentProps> = ({
  formattedTime,
  isOverLimit,
  isRunning,
  onPause,
  onResume,
  classes,
  labelLayout = 'stacked',
}) => {
  const controlButton = isRunning ? (
    <IconButton aria-label='Wstrzymaj timer' className={classes.controlButton} color='primary' onClick={onPause} size='small'>
      <PauseIcon fontSize='small' />
    </IconButton>
  ) : (
    <IconButton aria-label='Wznów timer' className={classes.controlButton} color='primary' onClick={onResume} size='small'>
      <PlayArrowIcon fontSize='small' />
    </IconButton>
  );

  if (labelLayout === 'inline') {
    return (
      <>
        <Typography className={classes.label} component='span'>
          Pozostały czas:
        </Typography>
        <div className={classes.content}>
          <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
            {formattedTime}
          </Typography>
          {controlButton}
        </div>
      </>
    );
  }

  return (
    <>
      <Typography className={classes.label} component='span'>
        Pozostały czas
      </Typography>
      <div className={classes.content}>
        <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
          {formattedTime}
        </Typography>
        {controlButton}
      </div>
    </>
  );
};

const SheetsTimerDisplay: React.FC<Props> = ({ isStuck = false, variant = 'desktop' }) => {
  const isActive = useIsSheetsTimerActive();
  const { durationMinutes, elapsedSeconds, isRunning, pause, resume } = useSheetsTimerStore();
  const classes = useStyles({ isStuck });

  const isOverLimit = useMemo(() => isSheetsTimerOverLimit(durationMinutes, elapsedSeconds), [durationMinutes, elapsedSeconds]);

  if (!isActive) {
    return null;
  }

  const formattedTime = formatRemainingTime(durationMinutes, elapsedSeconds);
  const timerContentProps = {
    formattedTime,
    isOverLimit,
    isRunning,
    onPause: pause,
    onResume: resume,
    classes,
  };

  if (variant === 'mobile-fixed') {
    return (
      <Portal>
        <div className={classes.mobileFixedWrapper}>
          <TimerContent {...timerContentProps} labelLayout='inline' />
        </div>
      </Portal>
    );
  }

  if (variant === 'pomoce') {
    return (
      <div className={classes.pomoceWrapper}>
        <TimerContent {...timerContentProps} />
      </div>
    );
  }

  return null;
};

export default SheetsTimerDisplay;
